import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useParams } from 'react-router-dom';

import usePublisher from 'api/usePublisher';
import { EPublisherSolutionModelSubType } from 'common/contracts';
import { permissionsUtil } from 'utils/permissionsUtil';

import { AnalyticsLoadingBox } from 'components/AnalyticsBox/AnalyticsLoadingBox';
import { MonthFilter } from 'components/FilterComponents/MonthFilter/MonthFilter';
import { getPreviousCalendarMonth } from 'components/FilterComponents/MonthFilter/MonthFilter.utils';
import { formatMonthYearLong } from 'components/MonthPicker/MonthPicker.utils';
import { Flex } from 'components/ui/flex';

import {
  buildPlaceholderApiMetrics,
  filterApiMetricsByVisibleApiNames,
  formatMonthForBackend,
  getVisibleApiNames
} from './AnalyticsApiPerformanceTab.utils';
import { ApiPerformanceCard } from './components/ApiPerformanceCard';
import { useApiPerformanceMetrics } from './hooks/useApiPerformanceMetrics';
import {
  EApiPerformanceFilterSection,
  useApiPerformanceTracking
} from './hooks/useApiPerformanceTracking';

const AnalyticsApiPerformanceTab: FC = () => {
  const { publisherId } = useParams();
  const maxSelectableMonth = useMemo(() => getPreviousCalendarMonth(), []);
  const [selectedMonth, setSelectedMonth] = useState<Date>(maxSelectableMonth);

  const { trackPageDisplayed, trackFilterApplied, isPublisherDataReady } =
    useApiPerformanceTracking();

  const hasTrackedPageDisplayed = useRef(false);

  useEffect(() => {
    if (!isPublisherDataReady || hasTrackedPageDisplayed.current) {
      return;
    }
    hasTrackedPageDisplayed.current = true;
    trackPageDisplayed();
  }, [isPublisherDataReady, trackPageDisplayed]);

  const handleSelectedMonthChange = useCallback(
    (nextSelectedMonth: Date) => {
      const isSameMonthSelected =
        formatMonthForBackend(nextSelectedMonth) ===
        formatMonthForBackend(selectedMonth);
      if (isSameMonthSelected) {
        return;
      }
      setSelectedMonth(nextSelectedMonth);
      trackFilterApplied({
        section: EApiPerformanceFilterSection.DATES,
        selection: formatMonthYearLong(nextSelectedMonth)
      });
    },
    [selectedMonth, trackFilterApplied]
  );

  const month = useMemo(
    () => formatMonthForBackend(selectedMonth),
    [selectedMonth]
  );

  const { data: apiPerformanceData, isLoading } = useApiPerformanceMetrics({
    publisherId,
    month
  });

  const { getPublisher } = usePublisher(publisherId || undefined);

  const isPaymentLinkProject =
    getPublisher?.data?.solutionModelSubtype ===
    EPublisherSolutionModelSubType.PAYMENT_LINKS;

  const isCheckoutOnlyPublisher =
    permissionsUtil.isCheckout() || isPaymentLinkProject;

  const visibleApiNames = useMemo(
    () => getVisibleApiNames(isCheckoutOnlyPublisher),
    [isCheckoutOnlyPublisher]
  );

  const hasApiMetrics = (apiPerformanceData?.apiMetrics?.length ?? 0) > 0;
  const shouldRenderLoadingPlaceholder = isLoading && !hasApiMetrics;

  const apiMetricsToRender = useMemo(() => {
    const responseApiMetrics = apiPerformanceData?.apiMetrics ?? [];
    if (responseApiMetrics.length > 0) {
      return filterApiMetricsByVisibleApiNames(
        responseApiMetrics,
        visibleApiNames
      );
    }
    return buildPlaceholderApiMetrics(visibleApiNames);
  }, [apiPerformanceData, visibleApiNames]);

  const apiPerformanceContent = useMemo(() => {
    if (shouldRenderLoadingPlaceholder) {
      return <AnalyticsLoadingBox />;
    }
    return apiMetricsToRender.map((apiMetric) => (
      <ApiPerformanceCard
        key={apiMetric.apiName}
        apiMetric={apiMetric}
        isLoading={isLoading}
      />
    ));
  }, [shouldRenderLoadingPlaceholder, apiMetricsToRender, isLoading]);

  return (
    <Flex className="pb-[57px]">
      <div className="w-full mt-1 mb-5 flex justify-start items-center">
        <MonthFilter
          dataTestId="api-performance-month-filter"
          selectedMonth={selectedMonth}
          onSelectedMonthChange={handleSelectedMonthChange}
          maxSelectableMonth={maxSelectableMonth}
        />
      </div>

      <div className="grid grid-cols-1 gap-4">{apiPerformanceContent}</div>
    </Flex>
  );
};

export default AnalyticsApiPerformanceTab;
